import { a as setChunkDiagnostics, c as setUnboundedDiagnostics, d as finalizeKnownGlobalMarkdownState, f as getKnownGlobalMarkdownState, g as countLines, h as runWithKnownGlobalMarkdownState, i as getParseDiagnostics, l as detectGlobalMarkdownState, m as resetKnownGlobalMarkdownState, n as beginParseDiagnostics, p as markKnownGlobalMarkdownState } from "./strategy_diagnostics-Bwt8UKDw.js";

//#region src/stream/chunked.ts
const DEFAULTS = {
	maxChunkChars: 1e4,
	maxChunkLines: 200,
	fenceAware: true,
	maxChunks: void 0,
	fallbackOnGlobalState: true
};
/**
* Chunk a markdown document on reasonably safe boundaries (blank-line separated)
* and parse each chunk separately, then merge token streams with line map offsets.
*
* @experimental Markdown is not always chunk-local. The default path falls back
* to a full parse for known document-level state and unsafe chunk boundaries;
* disabling those fallbacks can produce output that differs from full parsing.
*/
function chunkedParse(md, src, env = {}, opts) {
	beginParseDiagnostics(env);
	const options = {
		...DEFAULTS,
		...opts || {}
	};
	const currentGlobalStateReason = detectGlobalMarkdownState(src);
	if (options.fallbackOnGlobalState !== false && currentGlobalStateReason) {
		setChunkDiagnostics(env, {
			count: 1,
			fallback: true,
			fallbackReason: currentGlobalStateReason,
			globalStateDetected: currentGlobalStateReason,
			maxChunkChars: options.maxChunkChars,
			maxChunkLines: options.maxChunkLines
		});
		return runWithKnownGlobalMarkdownState(env, currentGlobalStateReason, () => {
			return md.core.parse(src, env, md).tokens;
		});
	}
	let ranges = splitIntoChunkRanges(src, options);
	if (options.maxChunks && ranges.length > options.maxChunks) ranges = rebalanceChunkRanges(ranges, options.maxChunks);
	if (hasUnsafeChunkBoundary(src, ranges)) {
		setChunkDiagnostics(env, {
			count: 1,
			fallback: true,
			fallbackReason: "unsafe-chunk-boundary",
			maxChunkChars: options.maxChunkChars,
			maxChunkLines: options.maxChunkLines
		});
		return runWithKnownGlobalMarkdownState(env, currentGlobalStateReason, () => {
			return md.core.parse(src, env, md).tokens;
		});
	}
	let lineOffset = 0;
	const out = [];
	setChunkDiagnostics(env, {
		count: ranges.length,
		maxChunkChars: options.maxChunkChars,
		maxChunkLines: options.maxChunkLines,
		globalStateDetected: currentGlobalStateReason || void 0,
		globalStateFallbackDisabled: options.fallbackOnGlobalState === false && !!currentGlobalStateReason
	});
	return runWithKnownGlobalMarkdownState(env, currentGlobalStateReason, () => {
		for (let i = 0; i < ranges.length; i++) {
			const range = ranges[i];
			const ch = src.slice(range.start, range.end);
			const tokens = md.core.parse(ch, env, md).tokens;
			if (lineOffset !== 0 && tokens.length) shiftTokenLines$1(tokens, lineOffset);
			appendTokens$1(out, tokens);
			lineOffset += range.lineCount;
		}
		return out;
	});
}
/**
* Split text into chunks by blank lines without breaking fenced code blocks.
* Keeps chunk sizes under maxChunkChars/maxChunkLines where possible.
*/
function splitIntoChunks(src, opts) {
	const ranges = splitIntoChunkRanges(src, opts);
	const chunks = new Array(ranges.length);
	for (let i = 0; i < ranges.length; i++) chunks[i] = src.slice(ranges[i].start, ranges[i].end);
	return chunks;
}
function splitIntoChunkRanges(src, opts, final = true) {
	const chunks = [];
	let charCount = 0;
	let lineCount = 0;
	let chunkStart = 0;
	let chunkLines = 0;
	let sinceBlankLines = 0;
	let sinceBlankChars = 0;
	let inFence = null;
	function flush(end) {
		if (end <= chunkStart) return;
		chunks.push({
			start: chunkStart,
			end,
			lineCount: chunkLines
		});
		chunkStart = end;
		charCount = 0;
		lineCount = 0;
		chunkLines = 0;
	}
	for (let lineStart = 0; lineStart < src.length;) {
		let lineEnd = src.indexOf("\n", lineStart);
		let lineEndWithNl = lineEnd;
		if (lineEnd === -1) {
			lineEnd = src.length;
			lineEndWithNl = src.length;
		} else lineEndWithNl = lineEnd + 1;
		const blank = isBlankLine$1(src, lineStart, lineEnd);
		if (opts.fenceAware) {
			let p = lineStart;
			while (p < lineEnd) {
				const c = src.charCodeAt(p);
				if (c === 32 || c === 9) p++;
				else break;
			}
			const ch = src[p];
			if (ch === "`" || ch === "~") {
				let q = p;
				while (q < lineEnd && src[q] === ch) q++;
				const runLen = q - p;
				if (runLen >= 3) {
					if (!inFence) inFence = {
						marker: ch,
						length: runLen
					};
					else if (inFence.marker === ch && runLen >= inFence.length) inFence = null;
				}
			}
		}
		const lineWithNlLen = lineEndWithNl - lineStart;
		charCount += lineWithNlLen;
		lineCount += 1;
		chunkLines += 1;
		if (blank) {
			sinceBlankLines = 0;
			sinceBlankChars = 0;
		} else {
			sinceBlankLines += 1;
			sinceBlankChars += lineWithNlLen;
		}
		const atBlankBoundary = blank;
		if ((charCount >= opts.maxChunkChars || lineCount >= opts.maxChunkLines) && !inFence) if (atBlankBoundary) flush(lineEndWithNl);
		else {
			const maxSinceBlankLines = Math.max(10, Math.floor(opts.maxChunkLines * .5));
			const maxSinceBlankChars = Math.max(opts.maxChunkChars, 8e3);
			if (sinceBlankLines >= maxSinceBlankLines || sinceBlankChars >= maxSinceBlankChars) flush(lineEndWithNl);
		}
		lineStart = lineEndWithNl;
	}
	if (final) flush(src.length);
	return chunks;
}
function hasUnsafeChunkBoundary(src, ranges, options = { rangesCoverWholeSource: true }) {
	const limit = options.rangesCoverWholeSource ? ranges.length - 1 : ranges.length;
	for (let i = 0; i < limit; i++) if (!endsAtBlankLine(src, ranges[i].end)) return true;
	return false;
}
function endsAtBlankLine(src, end) {
	if (end <= 0 || end > src.length || src.charCodeAt(end - 1) !== 10) return false;
	let lineStart = end - 2;
	while (lineStart >= 0 && src.charCodeAt(lineStart) !== 10) lineStart--;
	for (let i = lineStart + 1; i < end - 1; i++) {
		const ch = src.charCodeAt(i);
		if (ch !== 32 && ch !== 9 && ch !== 13) return false;
	}
	return true;
}
function shiftTokenLines$1(tokens, offset) {
	if (offset === 0) return;
	const stack = [];
	for (let i = tokens.length - 1; i >= 0; i--) stack.push(tokens[i]);
	while (stack.length) {
		const t = stack.pop();
		if (t.map) {
			t.map[0] += offset;
			t.map[1] += offset;
		}
		if (t.children) for (let i = t.children.length - 1; i >= 0; i--) stack.push(t.children[i]);
	}
}
function appendTokens$1(out, tokens) {
	for (let i = 0; i < tokens.length; i++) out.push(tokens[i]);
}
function rebalanceChunkRanges(chunks, maxChunks) {
	if (chunks.length <= maxChunks) return chunks;
	const out = [];
	let index = 0;
	for (let group = 0; group < maxChunks; group++) {
		const groupsLeft = maxChunks - group;
		const chunksLeft = chunks.length - index;
		const take = Math.ceil(chunksLeft / groupsLeft);
		const slice = chunks.slice(index, index + take);
		let lineCount = 0;
		for (let i = 0; i < slice.length; i++) lineCount += slice[i].lineCount;
		out.push({
			start: slice[0].start,
			end: slice[slice.length - 1].end,
			lineCount
		});
		index += take;
	}
	return out;
}
function isBlankLine$1(src, start, end) {
	for (let i = start; i < end; i++) {
		const ch = src.charCodeAt(i);
		if (ch !== 32 && ch !== 9 && ch !== 13) return false;
	}
	return true;
}

//#endregion
//#region src/stream/unbounded.ts
const DEFAULT_AUTO_UNBOUNDED_THRESHOLD_CHARS = 4e6;
const DEFAULT_AUTO_UNBOUNDED_THRESHOLD_LINES = 8e4;
const DEFAULT_FULL_CHUNK_CHARS = 1e4;
const DEFAULT_FULL_CHUNK_LINES = 200;
const DEFAULT_STREAM_CHUNK_CHARS = 1e4;
const DEFAULT_STREAM_CHUNK_LINES = 200;
function appendTokens(out, tokens) {
	for (let i = 0; i < tokens.length; i++) out.push(tokens[i]);
}
function shiftTokenLines(tokens, offset) {
	if (offset === 0) return;
	const stack = [];
	for (let i = tokens.length - 1; i >= 0; i--) stack.push(tokens[i]);
	while (stack.length) {
		const token = stack.pop();
		if (token.map) {
			token.map[0] += offset;
			token.map[1] += offset;
		}
		if (token.children) for (let i = token.children.length - 1; i >= 0; i--) stack.push(token.children[i]);
	}
}
function estimateLines(src) {
	if (src.length === 0) return 0;
	return countLines(src) + (src.charCodeAt(src.length - 1) === 10 ? 0 : 1);
}
function isBlankLine(src, start, end) {
	for (let i = start; i < end; i++) {
		const ch = src.charCodeAt(i);
		if (ch !== 32 && ch !== 9 && ch !== 13) return false;
	}
	return true;
}
function endsInsideFence(src, fenceAware) {
	if (!fenceAware || src.length === 0) return false;
	let inFence = null;
	for (let lineStart = 0; lineStart < src.length;) {
		let lineEnd = src.indexOf("\n", lineStart);
		if (lineEnd === -1) lineEnd = src.length;
		let p = lineStart;
		while (p < lineEnd) {
			const c = src.charCodeAt(p);
			if (c === 32 || c === 9) p++;
			else break;
		}
		const ch = src[p];
		if (ch === "`" || ch === "~") {
			let q = p;
			while (q < lineEnd && src[q] === ch) q++;
			const runLen = q - p;
			if (runLen >= 3) {
				if (!inFence) inFence = {
					marker: ch,
					length: runLen
				};
				else if (inFence.marker === ch && runLen >= inFence.length) inFence = null;
			}
		}
		lineStart = lineEnd === src.length ? src.length : lineEnd + 1;
	}
	return inFence !== null;
}
function endsAtBlankBoundary(src, fenceAware) {
	if (src.length === 0 || src.charCodeAt(src.length - 1) !== 10) return false;
	let prevNl = src.length - 2;
	while (prevNl >= 0 && src.charCodeAt(prevNl) !== 10) prevNl--;
	if (!isBlankLine(src, prevNl + 1, src.length - 1)) return false;
	return !endsInsideFence(src, fenceAware);
}
function resolveWindow(md, totalChars, totalLines, opts = {}) {
	const mode = opts.mode ?? "full";
	const fenceAware = opts.fenceAware ?? (mode === "stream" ? md.options.streamChunkFenceAware ?? true : md.options.fullChunkFenceAware ?? true);
	if (opts.maxChunkChars !== void 0 || opts.maxChunkLines !== void 0 || opts.autoTune === false) {
		const maxChunkChars = opts.maxChunkChars ?? (mode === "stream" ? md.options.streamChunkSizeChars ?? DEFAULT_STREAM_CHUNK_CHARS : md.options.fullChunkSizeChars ?? DEFAULT_FULL_CHUNK_CHARS);
		const maxChunkLines = opts.maxChunkLines ?? (mode === "stream" ? md.options.streamChunkSizeLines ?? DEFAULT_STREAM_CHUNK_LINES : md.options.fullChunkSizeLines ?? DEFAULT_FULL_CHUNK_LINES);
		return {
			maxChunkChars,
			maxChunkLines,
			holdBelowChars: maxChunkChars,
			holdBelowLines: maxChunkLines,
			fenceAware
		};
	}
	if (mode === "stream") {
		if (totalChars <= 5e3) return {
			maxChunkChars: 16e3,
			maxChunkLines: 250,
			holdBelowChars: 16e3,
			holdBelowLines: 250,
			fenceAware
		};
		if (totalChars <= 2e4) return {
			maxChunkChars: 16e3,
			maxChunkLines: 200,
			holdBelowChars: 16e3,
			holdBelowLines: 200,
			fenceAware
		};
		if (totalChars <= 5e4) return {
			maxChunkChars: 16e3,
			maxChunkLines: 250,
			holdBelowChars: 16e3,
			holdBelowLines: 250,
			fenceAware
		};
		if (totalChars <= 5e5) return {
			maxChunkChars: 32e3,
			maxChunkLines: 350,
			holdBelowChars: 32e3,
			holdBelowLines: 350,
			fenceAware
		};
		return {
			maxChunkChars: 64e3,
			maxChunkLines: 700,
			holdBelowChars: 64e3,
			holdBelowLines: 700,
			fenceAware
		};
	}
	if (totalChars <= 1e5 && totalLines <= 2500) return {
		maxChunkChars: 32e3,
		maxChunkLines: 350,
		holdBelowChars: 1e5,
		holdBelowLines: 2500,
		fenceAware
	};
	if (totalChars <= 2e5) return {
		maxChunkChars: 2e4,
		maxChunkLines: 150,
		holdBelowChars: 2e4,
		holdBelowLines: 150,
		fenceAware
	};
	if (totalChars <= 5e5) return {
		maxChunkChars: 32e3,
		maxChunkLines: 350,
		holdBelowChars: 32e3,
		holdBelowLines: 350,
		fenceAware
	};
	return {
		maxChunkChars: 64e3,
		maxChunkLines: 700,
		holdBelowChars: 64e3,
		holdBelowLines: 700,
		fenceAware
	};
}
/**
* Append-only parser for sources that already arrive as chunks.
*
* @experimental Streaming output can be committed before future document-level
* definitions are known. Use full-string parsing when exact full-parse parity
* matters for references, footnotes, abbreviations, or plugin global state.
*/
var UnboundedBuffer = class {
	md;
	options;
	pending = "";
	tokens = [];
	committedChars = 0;
	committedLines = 0;
	fedChunks = 0;
	parsedChunks = 0;
	globalStateEnv = null;
	markedGlobalStateReason = null;
	constructor(md, opts = {}) {
		this.md = md;
		this.options = {
			mode: "full",
			autoTune: true,
			retainTokens: true,
			...opts
		};
		if (this.options.retainTokens === false && !this.options.onChunkTokens) throw new Error("UnboundedBuffer with retainTokens=false requires onChunkTokens");
	}
	feed(chunk) {
		if (!chunk) return;
		this.pending += chunk;
		this.fedChunks += 1;
	}
	flushAvailable(env = {}) {
		if (!this.pending) return null;
		const window = this.resolveWindow();
		const pendingLines = estimateLines(this.pending);
		if (this.pending.length < window.holdBelowChars && pendingLines < window.holdBelowLines) {
			this.updateEnvDiagnostics(env, window, pendingLines);
			return null;
		}
		const ranges = splitIntoChunkRanges(this.pending, {
			maxChunkChars: window.maxChunkChars,
			maxChunkLines: window.maxChunkLines,
			fenceAware: window.fenceAware,
			maxChunks: void 0
		}, false);
		if (!ranges.length) {
			this.updateEnvDiagnostics(env, window, pendingLines);
			return null;
		}
		if (hasUnsafeChunkBoundary(this.pending, ranges, { rangesCoverWholeSource: false })) {
			this.updateEnvDiagnostics(env, window, pendingLines);
			return null;
		}
		const consumed = this.commitRanges(ranges, env);
		this.pending = this.pending.slice(consumed);
		this.updateEnvDiagnostics(env, window, estimateLines(this.pending));
		return this.tokens;
	}
	flushIfBoundary(env = {}) {
		if (!this.pending) return null;
		const window = this.resolveWindow();
		if (!endsAtBlankBoundary(this.pending, window.fenceAware)) {
			this.updateEnvDiagnostics(env, window, estimateLines(this.pending));
			return null;
		}
		const ranges = splitIntoChunkRanges(this.pending, {
			maxChunkChars: window.maxChunkChars,
			maxChunkLines: window.maxChunkLines,
			fenceAware: window.fenceAware,
			maxChunks: void 0
		}, true);
		if (!ranges.length) {
			this.updateEnvDiagnostics(env, window, estimateLines(this.pending));
			return null;
		}
		const rangesToCommit = hasUnsafeChunkBoundary(this.pending, ranges, { rangesCoverWholeSource: true }) ? [{
			start: 0,
			end: this.pending.length,
			lineCount: estimateLines(this.pending)
		}] : ranges;
		this.commitRanges(rangesToCommit, env);
		this.pending = "";
		this.updateEnvDiagnostics(env, window, 0);
		return this.tokens;
	}
	flushForce(env = {}) {
		if (!this.pending) {
			this.prepareGlobalStateEnv(env, "");
			const window$1 = this.resolveWindow();
			this.updateEnvDiagnostics(env, window$1, 0);
			return this.tokens;
		}
		const window = this.resolveWindow();
		const ranges = splitIntoChunkRanges(this.pending, {
			maxChunkChars: window.maxChunkChars,
			maxChunkLines: window.maxChunkLines,
			fenceAware: window.fenceAware,
			maxChunks: void 0
		}, true);
		if (ranges.length) {
			const rangesToCommit = hasUnsafeChunkBoundary(this.pending, ranges, { rangesCoverWholeSource: true }) ? [{
				start: 0,
				end: this.pending.length,
				lineCount: estimateLines(this.pending)
			}] : ranges;
			this.commitRanges(rangesToCommit, env);
			this.pending = "";
		}
		this.updateEnvDiagnostics(env, window, 0);
		return this.tokens;
	}
	reset() {
		this.pending = "";
		this.tokens = [];
		this.committedChars = 0;
		this.committedLines = 0;
		this.fedChunks = 0;
		this.parsedChunks = 0;
		this.globalStateEnv = null;
		this.markedGlobalStateReason = null;
	}
	peek() {
		return this.tokens;
	}
	pendingText() {
		return this.pending;
	}
	stats() {
		return {
			mode: this.options.mode ?? "full",
			fedChunks: this.fedChunks,
			parsedChunks: this.parsedChunks,
			committedChars: this.committedChars,
			committedLines: this.committedLines,
			pendingChars: this.pending.length,
			pendingLines: estimateLines(this.pending),
			retainedTokens: this.options.retainTokens !== false
		};
	}
	resolveWindow() {
		const totalChars = this.committedChars + this.pending.length;
		const totalLines = this.committedLines + estimateLines(this.pending);
		return resolveWindow(this.md, totalChars, totalLines, this.options);
	}
	prepareGlobalStateEnv(env, srcAboutToParse) {
		if (this.globalStateEnv !== env) {
			if (getKnownGlobalMarkdownState(env)) resetKnownGlobalMarkdownState(env);
			this.globalStateEnv = env;
			this.markedGlobalStateReason = null;
		}
		if (this.markedGlobalStateReason) return;
		const reason = detectGlobalMarkdownState(srcAboutToParse);
		if (!reason) return;
		markKnownGlobalMarkdownState(env, reason);
		this.markedGlobalStateReason = reason;
	}
	commitRanges(ranges, env) {
		if (!ranges.length) return 0;
		this.prepareGlobalStateEnv(env, this.pending);
		let consumed = 0;
		try {
			for (let i = 0; i < ranges.length; i++) {
				const range = ranges[i];
				const src = this.pending.slice(range.start, range.end);
				const nextTokens = this.md.core.parse(src, env, this.md).tokens;
				const startOffset = this.committedChars;
				const startLine = this.committedLines;
				if (startLine !== 0 && nextTokens.length) shiftTokenLines(nextTokens, startLine);
				if (this.options.retainTokens !== false) appendTokens(this.tokens, nextTokens);
				this.committedChars += src.length;
				this.committedLines += range.lineCount;
				this.parsedChunks += 1;
				if (this.options.onChunkTokens) this.options.onChunkTokens(nextTokens, {
					chunkIndex: this.parsedChunks,
					chunkChars: src.length,
					chunkLines: range.lineCount,
					tokenCount: nextTokens.length,
					startOffset,
					endOffset: this.committedChars,
					startLine,
					endLine: this.committedLines
				});
				consumed = range.end;
			}
			if (this.markedGlobalStateReason) finalizeKnownGlobalMarkdownState(env);
			return consumed;
		} catch (error) {
			if (this.markedGlobalStateReason) {
				resetKnownGlobalMarkdownState(env);
				this.globalStateEnv = null;
				this.markedGlobalStateReason = null;
			}
			throw error;
		}
	}
	updateEnvDiagnostics(env, window, pendingLines) {
		setUnboundedDiagnostics(env, {
			mode: this.options.mode ?? "full",
			maxChunkChars: window.maxChunkChars,
			maxChunkLines: window.maxChunkLines,
			committedChars: this.committedChars,
			committedLines: this.committedLines,
			pendingChars: this.pending.length,
			pendingLines,
			fedChunks: this.fedChunks,
			parsedChunks: this.parsedChunks,
			globalStateDetected: this.markedGlobalStateReason || void 0
		});
	}
};
/**
* Parse an iterable chunk source without first joining all chunks.
*
* @experimental This is for explicit chunk-stream inputs. It may flush earlier
* chunks before later document-level definitions are observed.
*/
function parseIterable(md, chunks, env = {}, opts = {}) {
	beginParseDiagnostics(env);
	const buffer = new UnboundedBuffer(md, {
		mode: "full",
		...opts
	});
	for (const chunk of chunks) {
		buffer.feed(chunk);
		buffer.flushAvailable(env);
	}
	return buffer.flushForce(env);
}
/**
* Parse an async iterable chunk source without first joining all chunks.
*
* @experimental This is for explicit chunk-stream inputs. It may flush earlier
* chunks before later document-level definitions are observed.
*/
async function parseAsyncIterable(md, chunks, env = {}, opts = {}) {
	beginParseDiagnostics(env);
	const buffer = new UnboundedBuffer(md, {
		mode: "full",
		...opts
	});
	for await (const chunk of chunks) {
		buffer.feed(chunk);
		buffer.flushAvailable(env);
	}
	return buffer.flushForce(env);
}
/**
* Parse iterable chunks and deliver token chunks to a sink.
*
* @experimental Sink output is streaming-oriented and can differ from a final
* full parse when future document-level definitions affect earlier text.
*/
function parseIterableToSink(md, chunks, onChunkTokens, env = {}, opts = {}) {
	beginParseDiagnostics(env);
	const buffer = new UnboundedBuffer(md, {
		mode: "full",
		...opts,
		retainTokens: false,
		onChunkTokens
	});
	for (const chunk of chunks) {
		buffer.feed(chunk);
		buffer.flushAvailable(env);
	}
	buffer.flushForce(env);
	return buffer.stats();
}
/**
* Parse async iterable chunks and deliver token chunks to a sink.
*
* @experimental Sink output is streaming-oriented and can differ from a final
* full parse when future document-level definitions affect earlier text.
*/
async function parseAsyncIterableToSink(md, chunks, onChunkTokens, env = {}, opts = {}) {
	beginParseDiagnostics(env);
	const buffer = new UnboundedBuffer(md, {
		mode: "full",
		...opts,
		retainTokens: false,
		onChunkTokens
	});
	for await (const chunk of chunks) {
		buffer.feed(chunk);
		buffer.flushAvailable(env);
	}
	buffer.flushForce(env);
	return buffer.stats();
}
function shouldAutoUseUnbounded(md, totalChars, totalLines) {
	if (md.options.autoUnbounded === false) return false;
	const thresholdChars = md.options.autoUnboundedThresholdChars ?? DEFAULT_AUTO_UNBOUNDED_THRESHOLD_CHARS;
	const thresholdLines = md.options.autoUnboundedThresholdLines ?? DEFAULT_AUTO_UNBOUNDED_THRESHOLD_LINES;
	return totalChars >= thresholdChars || totalLines >= thresholdLines;
}
function getAutoUnboundedDecision(md, totalChars, totalLines) {
	if (md.options.autoUnbounded === false) return "no";
	if (totalChars >= (md.options.autoUnboundedThresholdChars ?? DEFAULT_AUTO_UNBOUNDED_THRESHOLD_CHARS)) return "yes";
	const thresholdLines = md.options.autoUnboundedThresholdLines ?? DEFAULT_AUTO_UNBOUNDED_THRESHOLD_LINES;
	if (totalLines !== void 0) return totalLines >= thresholdLines ? "yes" : "no";
	if (totalChars + 1 < thresholdLines) return "no";
	return "need-lines";
}
/**
* Parse a complete string through the unbounded chunking path.
*
* @experimental Defaults to correctness-first fallback for known global-state
* constructs. Disabling the fallback is performance-oriented and can diverge
* from normal full parsing.
*/
function parseStringUnbounded(md, src, env = {}, opts = {}) {
	beginParseDiagnostics(env);
	const currentGlobalStateReason = detectGlobalMarkdownState(src);
	if (getKnownGlobalMarkdownState(env)) resetKnownGlobalMarkdownState(env);
	if (opts.fallbackOnGlobalState !== false && currentGlobalStateReason) {
		setUnboundedDiagnostics(env, {
			mode: "full",
			fallback: true,
			fallbackReason: currentGlobalStateReason,
			committedChars: src.length,
			committedLines: countLines(src),
			pendingChars: 0,
			pendingLines: 0,
			fedChunks: 1,
			parsedChunks: 1,
			globalStateDetected: currentGlobalStateReason
		});
		return runWithKnownGlobalMarkdownState(env, currentGlobalStateReason, () => {
			return md.core.parse(src, env, md).tokens;
		});
	}
	const tokens = [];
	const buffer = new UnboundedBuffer(md, {
		mode: "full",
		...opts,
		retainTokens: false,
		onChunkTokens(nextTokens) {
			appendTokens(tokens, nextTokens);
		}
	});
	if (currentGlobalStateReason) markKnownGlobalMarkdownState(env, currentGlobalStateReason);
	buffer.feed(src);
	buffer.flushForce(env);
	if (currentGlobalStateReason) {
		finalizeKnownGlobalMarkdownState(env);
		if (opts.fallbackOnGlobalState === false) {
			const info = getParseDiagnostics(env)?.unbounded;
			if (info) {
				info.globalStateDetected = currentGlobalStateReason;
				info.globalStateFallbackDisabled = true;
			}
		}
	}
	return tokens;
}

//#endregion
//#region src/support/chunk_recommend.ts
const clamp = (v, lo, hi) => v < lo ? lo : v > hi ? hi : v;
function normalizeOptions(opts) {
	return opts.experimental ? {
		...opts,
		...opts.experimental
	} : opts;
}
const FULL_DISCRETE_RECOMMENDATIONS = [
	{
		max: 5e3,
		strategy: "discrete",
		maxChunkChars: 32e3,
		maxChunkLines: 150,
		maxChunks: 8,
		notes: "<=5k"
	},
	{
		max: 2e4,
		strategy: "discrete",
		maxChunkChars: 24e3,
		maxChunkLines: 200,
		maxChunks: 12,
		notes: "<=20k"
	},
	{
		max: 1e5,
		strategy: "plain",
		notes: "<=100k plain"
	},
	{
		max: 2e5,
		strategy: "discrete",
		maxChunkChars: 2e4,
		maxChunkLines: 150,
		maxChunks: 12,
		notes: "<=200k"
	},
	{
		max: 5e5,
		strategy: "discrete",
		maxChunkChars: 64e3,
		maxChunkLines: 700,
		maxChunks: 16,
		notes: "<=500k"
	},
	{
		max: 5e6,
		strategy: "discrete",
		maxChunkChars: 64e3,
		maxChunkLines: 700,
		maxChunks: 16,
		notes: "<=5M"
	}
];
const STREAM_DISCRETE_RECOMMENDATIONS = [
	{
		max: 5e3,
		strategy: "discrete",
		maxChunkChars: 16e3,
		maxChunkLines: 250,
		maxChunks: 8,
		notes: "<=5k"
	},
	{
		max: 2e4,
		strategy: "discrete",
		maxChunkChars: 2e4,
		maxChunkLines: 200,
		maxChunks: 24,
		notes: "<=20k"
	},
	{
		max: 1e5,
		strategy: "discrete",
		maxChunkChars: 2e4,
		maxChunkLines: 200,
		maxChunks: 24,
		notes: "<=100k"
	},
	{
		max: 5e5,
		strategy: "discrete",
		maxChunkChars: 64e3,
		maxChunkLines: 700,
		maxChunks: 32,
		notes: "<=500k"
	},
	{
		max: 5e6,
		strategy: "discrete",
		maxChunkChars: 64e3,
		maxChunkLines: 700,
		maxChunks: 32,
		notes: "<=5M"
	}
];
function toRecommendation(fenceAware, discrete) {
	return {
		strategy: discrete.strategy,
		maxChunkChars: discrete.maxChunkChars,
		maxChunkLines: discrete.maxChunkLines,
		maxChunks: discrete.maxChunks,
		fenceAware,
		notes: discrete.notes
	};
}
/**
* Suggest full-parse chunk settings for the current synthetic harness defaults.
*
* @experimental Recommendations are workload-dependent; validate on the corpus
* you plan to parse.
*/
function recommendFullChunkStrategy(sizeChars, sizeLines = Math.max(0, sizeChars / 40 | 0), opts = {}) {
	const options = normalizeOptions(opts);
	const fenceAware = options.fullChunkFenceAware ?? true;
	const target = options.fullChunkTargetChunks ?? 8;
	const adaptive = options.fullChunkAdaptive !== false;
	for (let i = 0; i < FULL_DISCRETE_RECOMMENDATIONS.length; i++) {
		const rec = FULL_DISCRETE_RECOMMENDATIONS[i];
		if (sizeChars <= rec.max) {
			if (rec.strategy !== "adaptive") return toRecommendation(fenceAware, rec);
			break;
		}
	}
	if (sizeChars > 5e6) return {
		strategy: "plain",
		fenceAware,
		notes: ">5M plain"
	};
	if (adaptive) return {
		strategy: "adaptive",
		maxChunkChars: clamp(Math.ceil(sizeChars / target), 8e3, 64e3),
		maxChunkLines: clamp(Math.ceil(sizeLines / target), 150, 700),
		maxChunks: clamp(Math.ceil(sizeChars / 64e3), target, 16),
		fenceAware,
		notes: "adaptive fallback"
	};
	return {
		strategy: "discrete",
		maxChunkChars: options.fullChunkSizeChars ?? 1e4,
		maxChunkLines: options.fullChunkSizeLines ?? 200,
		fenceAware,
		maxChunks: options.fullChunkMaxChunks
	};
}
/**
* Suggest stream chunk settings for the current synthetic harness defaults.
*
* @experimental Recommendations are workload-dependent; validate on the corpus
* you plan to parse.
*/
function recommendStreamChunkStrategy(sizeChars, sizeLines = Math.max(0, sizeChars / 40 | 0), opts = {}) {
	const options = normalizeOptions(opts);
	const fenceAware = options.streamChunkFenceAware ?? true;
	const target = options.streamChunkTargetChunks ?? 8;
	const adaptive = options.streamChunkAdaptive !== false;
	for (let i = 0; i < STREAM_DISCRETE_RECOMMENDATIONS.length; i++) {
		const rec = STREAM_DISCRETE_RECOMMENDATIONS[i];
		if (sizeChars <= rec.max) {
			if (rec.strategy !== "adaptive") return toRecommendation(fenceAware, rec);
			break;
		}
	}
	if (sizeChars > 5e6) return {
		strategy: "plain",
		fenceAware,
		notes: ">5M plain"
	};
	if (adaptive) return {
		strategy: "adaptive",
		maxChunkChars: clamp(Math.ceil(sizeChars / target), 8e3, 64e3),
		maxChunkLines: clamp(Math.ceil(sizeLines / target), 150, 700),
		maxChunks: clamp(Math.ceil(sizeChars / 64e3), target, 32),
		fenceAware,
		notes: "adaptive fallback"
	};
	return {
		strategy: "discrete",
		maxChunkChars: options.streamChunkSizeChars ?? 1e4,
		maxChunkLines: options.streamChunkSizeLines ?? 200,
		maxChunks: options.streamChunkMaxChunks,
		fenceAware
	};
}

//#endregion
export { parseAsyncIterable as a, parseIterableToSink as c, chunkedParse as d, splitIntoChunkRanges as f, getAutoUnboundedDecision as i, parseStringUnbounded as l, recommendStreamChunkStrategy as n, parseAsyncIterableToSink as o, splitIntoChunks as p, UnboundedBuffer as r, parseIterable as s, recommendFullChunkStrategy as t, shouldAutoUseUnbounded as u };